"use client";

import {
  Appstore,
  Facebook,
  Instagram,
  LinkedIn,
  Playstore,
  Tiktok,
  Twitter,
  Youtube,
} from "@/components/bnp-icons";
import Image from "next/image";
import Link from "next/link";
import { CookieSettingsButton } from "@/components/cookies/CookieSettingsButton";
import { usePlatformLocations } from "@/hooks/usePlatformLocations";
import type { PlatformLocationCity } from "@/types/property";

interface FooterLink {
  text: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface FooterColumn {
  sections: FooterSection[];
}

function propertySearchHref(query: string): string {
  return `/properties?searchQuery=${encodeURIComponent(query)}`;
}

function topCities(cities: PlatformLocationCity[]): PlatformLocationCity[] {
  return [...cities]
    .filter((city) => city.listing_count > 0)
    .sort(
      (a, b) =>
        b.listing_count - a.listing_count || a.name.localeCompare(b.name),
    )
    .slice(0, 5);
}

const Footer: React.FC = () => {
  const { cities: lagosCities } = usePlatformLocations({ state: "lagos" });
  const { cities: abujaCities } = usePlatformLocations({ state: "abuja" });
  const topLagosCities = topCities(lagosCities);
  const topAbujaCities = topCities(abujaCities);

  const footerData: FooterColumn[] = [

    {
      sections: [
        {
          title: "EXPLORE BY STATE",
          links: [
            {
              text: "Properties in Lagos",
              href: "/properties/in/lagos",
            },
            {
              text: "Properties in Abuja (FCT)",
              href: "/properties/in/abuja",
            },
            {
              text: "Properties in Akwa Ibom",
              href: "/properties/in/akwa-ibom",
            },
            {
              text: "Properties in Kaduna",
              href: "/properties/in/kaduna",
            },
            {
              text: "Realtors in Lagos",
              href: "/realtors/state/lagos",
            },
          ],
        },
        {
          title: "BROWSE BY LISTING TYPE",
          links: [
            {
              text: "Properties for Sale",
              href: "/properties/sale",
            },
            {
              text: "Properties for Rent",
              href: "/properties/rent",
            },
            {
              text: "Short Lets",
              href: "/properties/shortlet",
            },
            {
              text: "Land for Sale",
              href: "/properties/sale/land",
            },
            {
              text: "Properties for Lease",
              href: "/properties/lease",
            },
          ],
        },
        {
          title: "POPULAR SEARCHES",
          links: [
            {
              text: "2 Bedrooms for Rent",
              href: propertySearchHref("2 bedrooms for rent"),
            },
            {
              text: "Houses for Sale in Lekki",
              href: propertySearchHref("house for sale in lekki"),
            },
            {
              text: "Houses for Rent in Ajah",
              href: propertySearchHref("house for rent in ajah"),
            },
            {
              text: "3 Bedrooms for Rent in Ajah",
              href: propertySearchHref("3 bedrooms for rent in ajah"),
            },
            {
              text: "4 Bedrooms for Rent in Ajah",
              href: propertySearchHref("4 bedrooms for rent in ajah"),
            },
          ],
        },
      ],
    },
    {
      sections: [
        ...(topLagosCities.length > 0
          ? [
            {
              title: "TOP LAGOS CITIES",
              links: topLagosCities.map((city) => ({
                text: `Properties in ${city.name}`,
                href: `/properties/in/lagos/${city.slug}`,
              })),
            },
          ]
          : []),
        ...(topAbujaCities.length > 0
          ? [
            {
              title: "TOP ABUJA CITIES",
              links: topAbujaCities.map((city) => ({
                text: `Properties in ${city.name}`,
                href: `/properties/in/abuja/${city.slug}`,
              })),
            },
          ]
          : []),
        {
          title: "ABOUT",
          links: [
            // { text: "Company", href: "/company" },
            // { text: "How it works", href: "/how-it-works" },
            { text: "Contact", href: "/contact" },
            // { text: "Developments", href: "/investments" },
          ],
        },
        {
          title: "TERMS & PRIVACY",
          links: [
            // { text: "FAQs", href: "/faqs" },
            { text: "Terms of Service", href: "/terms-and-conditions" },
            { text: "Privacy Policy", href: "/privacy-policy" },
            { text: "Cookie Policy", href: "/cookie-policy" },
          ],
        },
      ],
    },
        {
      sections: [
        {
          title: "QUICK LINKS",
          links: [
            { text: "Become a realtor", href: "/auth/sign-up" },
            { text: "Advertise/Promote property", href: "/contact" },
          ],
        },

        {
          title: "MANAGED DEVELOPMENTS",
          links: [
            { text: "Available development", href: "/managed-developments" },
            {
              text: "List development offerings",
              href: "/user/dashboard/properties/my-developments",
            },
            // { text: "Properties for investments", href: "/investments" },
          ],
        },

        {
          title: "MANAGED PROPERTIES",
          links: [
            {
              text: "Properties for Rent",
              href: "/managed-properties?listingType=rent",
            },
            {
              text: "Properties for Sale",
              href: "/managed-properties?listingType=sale",
            },
            {
              text: "Properties for Shortlets",
              href: "/managed-properties?listingType=shortlet",
            },
            // { text: "Properties for investments", href: "/investments" },
          ],
        },
      ],
    },
  ];

  const socialLinks = [
    {
      icon: <Facebook />,
      href: "https://www.facebook.com/findnigeriaproperties",
    },
    {
      icon: <Instagram />,
      href: "https://www.instagram.com/theofficialfnp",
    },
    { icon: <Twitter />, href: "https://www.x.com/theofficialfnp" },
    {
      icon: <Tiktok />,
      href: "https://www.tiktok.com/@theofficialfnp",
    },
    {
      icon: <LinkedIn />,
      href: "https://www.linkedin.com/company/officialfnp/?viewAsMember=true",
    },
    {
      icon: <Youtube />,
      href: "https://www.youtube.com/@theofficialfnp",
    },
  ];

  return (
    <footer className="bg-footer-dark py-16 px-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:justify-between gap-16">
          <div className="flex-shrink-0">
            <div className="">
              <Link href={"/"}>
                <img
                  src="/fnp-logo-with-credibility-subtext-white.png"
                  alt="FindNigeriaProperties.com"
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            <div className="mt-[60px]  lg:mt-[90px]">
              <p className="mb-2 text-sm font-medium text-base-white">
                Get our mobile app on
              </p>
              <div className="flex items-center  relative -left-5">
                <a
                  href="https://play.google.com/store/apps/details?id=com.fnp.appid"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  className="inline-flex items-center justify-center transition-transform hover:scale-105"
                >
                  <Playstore width={130} height={41} />
                </a>

                <a
                  href="https://apps.apple.com" // Replace with your actual App Store link
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                  className="inline-flex items-center justify-center transition-transform hover:scale-105"
                >
                  <Image
                    src="/assets/images/apple-store.png"
        width={90}
        height={28}
        alt="apple-store-logo"
        className="h-auto w-[90px] object-contain"
                  />
                </a>
              </div>
            </div>


          </div>

          <div className="">
            <div className="ml-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-32 gap-y-8 w-[100%]">
              {footerData.map((column, columnIndex) => (
                <div key={columnIndex} className="space-y-8">
                  {column.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="space-y-4">
                      <h3 className="text-base-white font-semibold text-sm">
                        {section.title}
                      </h3>
                      <ul className="space-y-2">
                        {section.links.map((link, linkIndex) => (
                          <li key={linkIndex}>
                            <Link
                              href={link.href}
                              className="text-base-white hover:text-base-white text-sm transition-colors duration-200"
                            >
                              {link.text}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-base-white text-sm text-center lg:text-start">
              ©2026 FindNigeriaProperties.com by Freshspring Property
              Development Company Limited &nbsp;|&nbsp; All rights reserved
              &nbsp;|&nbsp; <CookieSettingsButton />
            </div>

            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-grey-7 hover:text-base-white transition-colors duration-200"
                >
                  <span className="w-6 h-6 flex items-center justify-center">
                    <span className="text-grey-7">{social.icon}</span>
                    <div className="w-5 h-5" />
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
